import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  HostListener,
  OnDestroy,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AlertType, ButtonPurpose } from '@mhe/ngx-shared';
import { TTSStore } from '../../state/tts.store';
import * as ttsActions from '../../state/tts.actions';
import {
  debounceTime,
  distinctUntilChanged,
  first,
  map,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { BehaviorSubject, combineLatest, firstValueFrom, Subject } from 'rxjs';
import { TTSAudioContext, TTSPlaybackMode } from '@mhe/reader/models';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { UserSettingsStore } from '@mhe/reader/features/user-settings';
import * as userSettingsActions from '@mhe/reader/features/user-settings/state/user-settings.actions';
import { ReaderConfigStore } from '@mhe/reader/components/reader/state';

@Component({
  selector: 'rdrx-audio-controls',
  templateUrl: './audio-controls.component.html',
  styleUrls: ['./audio-controls.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AudioControlsComponent implements OnDestroy {
  @Input() showClose = true;
  @Input() showPlaybackScrubber = true;
  @Input() audioContext: TTSAudioContext;

  @Output() closeEvent = new EventEmitter<void>();

  @ViewChild('controlRow') controlRow: ElementRef;
  @ViewChild('playPauseButton', { read: ElementRef })
  playPauseButton: ElementRef;

  public ButtonPurpose = ButtonPurpose;
  public AlertType = AlertType;
  public playbackRateValues = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
  public playDebounceTime = 500;

  private readonly _debouncedPlay$ = new Subject<{
    playbackPosition?: number
  }>();

  private readonly _destroy$ = new Subject<void>();

  public playbackPosition$ = this.ttsStore.activeAudioPlaybackProgress$.pipe(
    map((p) => p?.playbackPosition ?? 0),
  );

  // External events like scrolling can stop playback.
  // We want that playing state to be reflected here.
  public isPlaying$ = this.ttsStore.activeAudioPlaybackProgress$.pipe(
    map((p) => p?.isPlaying ?? false),
  );

  /**
   * Playback error are errors emitted from the HTML audio mp3.
   */
  public hasPlaybackError$ = this.ttsStore.activeAudioPlaybackProgress$.pipe(
    map((p) => !!p?.error),
    distinctUntilChanged(),
  );

  /**
   * Translation error means the backend failed to provide audio chunks.
   */
  public hasTranslationError$ = this.ttsStore.activeAudio$.pipe(
    map((audioCollection) =>
      audioCollection
        ? audioCollection.chunkCollection?.chunks?.length === 0
        : false,
    ),
    distinctUntilChanged(),
  );

  /**
   * A steam to indicate that something generally went wrong.
   */
  public hasError$ = combineLatest([
    this.hasTranslationError$,
    this.hasPlaybackError$,
  ]).pipe(
    map(
      ([hasPlaybackError, hasTranslationError]) =>
        hasPlaybackError || hasTranslationError,
    ),
  );

  public totalPlaybackTime$ = this.ttsStore.activeAudioTotalPlaybackTime$;

  public volume$ = this.ttsStore.volume$;

  public isMuted$ = this.ttsStore.volume$.pipe(map((vol) => vol === 0));

  public playbackRate$ = this.ttsStore.playbackRate$;
  public formatPlaybackRateAria = (value: number): string => `${value}x`;

  /**
   * A stream of requests to toggle the settings dropdown.
   */
  private readonly settingsDropdownToggle$ = new BehaviorSubject<boolean>(false);

  public showSettingsDropDown$ = combineLatest([
    this.settingsDropdownToggle$,
    this.hasError$,
  ]).pipe(
    map(([isOpen, hasError]) => {
      if (hasError) {
        // hide settings on errors
        return false;
      }
      return isOpen;
    }),
  );

  /**
   * The effective auto page turn state for the checkbox.
   * Priority:
   * 1. If user setting is defined (true/false), use that value
   * 2. If user setting is undefined, derive from playback mode (fixed layout/k5 logic)
   */
  public effectiveAutoPageTurn$ = combineLatest([
    this.userSettingsStore.userSettings$,
    this.ttsStore.playbackMode$,
  ]).pipe(
    map(([userSettings, playbackMode]) => {
      // If user has explicitly set a preference, use it
      if (userSettings.autoPageTurn !== undefined) {
        return userSettings.autoPageTurn;
      }
      // Otherwise, derive from the computed playback mode
      return playbackMode === TTSPlaybackMode.AUTO_FLIP;
    }),
  );

  /**
   * Whether to show the auto page turn checkbox.
   * Only shown for:
   * - Page-level audio context (read aloud)
   * - Non-fixed layout books
   * - Non-K5 interface mode
   */
  public showAutoPageTurn$ = combineLatest([
    this.ttsStore.currentView$,
    this.readerConfigStore.interfaceMode$,
  ]).pipe(
    map(([currentView, interfaceMode]) => {
      const isPageContext = this.audioContext === TTSAudioContext.PAGE;
      const isFixedLayout = currentView.isFixedLayout === true;
      const isK5 = interfaceMode === 'k5';
      return isPageContext && !isFixedLayout && !isK5;
    }),
  );

  private unMutedVolume = 1;

  constructor(
    private readonly ttsStore: TTSStore,
    private readonly liveAnnouncer: LiveAnnouncer,
    private readonly userSettingsStore: UserSettingsStore,
    private readonly readerConfigStore: ReaderConfigStore,
  ) {
    // We need to debounce the play events here in the
    // component because if this audio controls component
    // is destroyed, then the play action delayed from the
    // debounce should not get dispatched.
    this._debouncedPlay$
      .pipe(
        debounceTime(this.playDebounceTime),
        tap(({ playbackPosition }) =>
          this.ttsStore.dispatch(
            ttsActions.play({ playbackPosition, context: this.audioContext }),
          ),
        ),
        takeUntil(this._destroy$),
      )
      .subscribe();
  }

  @HostListener('keydown.escape', ['$event'])
  onEscape(event: KeyboardEvent): void {
    // If a tooltip is present, allow the directive rdrxHideOnEscape to handle closing it first.
    const openTooltip = document.querySelector('.tooltip-content');
    if (openTooltip) {
      return;
    }
    this.onClose();
  }

  onTogglePlay(): void {
    this.isPlaying$.pipe(first()).subscribe((isPlaying) => {
      if (isPlaying) {
        this.ttsStore.dispatch(ttsActions.pause({}));
      } else {
        this._debouncedPlay$.next({});
      }
    });
  }

  onToggleMute(): void {
    this.volume$.pipe(first()).subscribe((volume) => {
      if (volume > 0) {
        // Store the volume when the user muted so we can restore it
        this.unMutedVolume = volume;
        // Mute the volume
        this.ttsStore.dispatch(ttsActions.updateVolume({ val: 0 }));
      } else {
        // The user clicked mute, so we'll restore that volume
        this.ttsStore.dispatch(
          ttsActions.updateVolume({ val: this.unMutedVolume }),
        );
      }
    });
  }

  onSkip(event): void {
    // Deboucing here is important so we don't trigger
    // an mp3 request for each play dispatch.
    void firstValueFrom(this.totalPlaybackTime$).then(totalPlaybackTime => {
      const nextPlaybackPosition = (totalPlaybackTime ?? 0) / 100 * parseInt(event.value);
      this._debouncedPlay$.next({ playbackPosition: nextPlaybackPosition });
    });
    // Announce the new progress position/value to screen readers.
    void this.liveAnnouncer.announce(this.calculateValueNow(event.value, 'progress'), 'assertive');
  }

  onPlaybackScrubberMove(): void {
    // The user is sliding, prevent the progress from fighting them.
    // This is going to be called rapidly with a mouse drag, but no performance
    // hit noticed.
    this.ttsStore.dispatch(ttsActions.stopProgressReporting());
  }

  onVolumeChange(event): void {
    this.ttsStore.dispatch(ttsActions.updateVolume({ val: event.value }));
    // Update store state immediately and persist to API
    this.userSettingsStore.setUserSettings({ ttsVolume: event.value });
    this.userSettingsStore.dispatch(
      userSettingsActions.setUserSettings({
        userSettings: { ttsVolume: event.value },
      }),
    );
    // Announce the new volume position/value to screen readers.
    void this.liveAnnouncer.announce(this.calculateValueNow(event.value), 'assertive');
  }

  onPlaybackRateChange(event): void {
    this.ttsStore.dispatch(
      ttsActions.updatePlaybackRate({
        val: event.target.value,
      }),
    );
    // Update store state immediately and persist to API
    this.userSettingsStore.setUserSettings({ ttsPlaybackRate: event.target.value });
    this.userSettingsStore.dispatch(
      userSettingsActions.setUserSettings({
        userSettings: { ttsPlaybackRate: event.target.value },
      }),
    );
  }

  onViewSettings(): void {
    this.settingsDropdownToggle$.pipe(first()).subscribe((isOpen) => {
      this.settingsDropdownToggle$.next(!isOpen);
    });
  }

  onAutoPageTurnChange(checked: boolean): void {
    // Update store state immediately and persist to API
    this.userSettingsStore.setUserSettings({ autoPageTurn: checked });
    this.userSettingsStore.dispatch(
      userSettingsActions.setUserSettings({
        userSettings: { autoPageTurn: checked },
      }),
    );
  }

  onClose(): void {
    this.closeEvent.emit();
  }

  onCloseSettings(event: KeyboardEvent): void {
    if (document.querySelector('.tooltip-content')) {
      return;
    }

    event.stopPropagation();
    this.showSettingsDropDown$.pipe(first()).subscribe((isOpen) => {
      if (isOpen) {
        this.settingsDropdownToggle$.next(false);
        this.controlRow?.nativeElement
          ?.querySelector('.settings-toggle')
          ?.focus();
      }
    });
  }

  calculateValueNow(volumeValue: number, suffix = 'volume'): string {
    return `${Math.round(volumeValue)}% ${suffix}`;
  }

  round(decimal: number): number {
    return Math.round(decimal);
  }

  ngOnDestroy(): void {
    // don't allow audio to play without controls
    this.ttsStore.dispatch(ttsActions.pause({}));
    this._destroy$.next();
    this._destroy$.complete();
  }
}
